import { ArrowDown, ArrowUp, ClipboardList, Plus, UploadCloud, X } from 'lucide-react'
import { QuestionTypeEnum } from '@/gen/cortado/v1/questionnaires_pb'
import {
  BOOKING_QUESTION_TYPES,
  bookingQuestionNeedsOptions,
  createDraftItem,
  createDraftOption,
  questionWidgetKind,
  type BookingQuestionnaireDraftItem,
  withQuestionType,
} from '@/lib/booking-questionnaire'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Empty, EmptyDescription, EmptyMedia } from '@/components/ui/empty'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { Switch } from '@/components/ui/switch'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Textarea } from '@/components/ui/textarea'

function moveItem<T>(items: T[], index: number, direction: 'up' | 'down'): T[] {
  const target = direction === 'up' ? index - 1 : index + 1
  if (target < 0 || target >= items.length) return items
  const next = [...items]
  const [removed] = next.splice(index, 1)
  next.splice(target, 0, removed)
  return next
}

function updateItem(
  items: BookingQuestionnaireDraftItem[],
  id: string,
  patch: Partial<BookingQuestionnaireDraftItem> | ((item: BookingQuestionnaireDraftItem) => BookingQuestionnaireDraftItem),
): BookingQuestionnaireDraftItem[] {
  return items.map((item) => {
    if (item.id !== id) return item
    return typeof patch === 'function' ? patch(item) : { ...item, ...patch }
  })
}

export function BookingQuestionnaireDesigner({
  items,
  onChange,
  label = 'Booking questionnaire',
  description = 'These questions are copied onto new bookings of this type. Editing them later does not change bookings that already exist.',
}: {
  items: BookingQuestionnaireDraftItem[]
  onChange: (items: BookingQuestionnaireDraftItem[]) => void
  label?: string
  description?: string
}) {
  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-1">
        <Label>{label}</Label>
        <p className="text-sm text-muted-foreground">
          {description}
        </p>
      </div>

      <Tabs defaultValue="design">
        <TabsList>
          <TabsTrigger value="design">Design</TabsTrigger>
          <TabsTrigger value="preview">Preview</TabsTrigger>
        </TabsList>
        <TabsContent value="design" className="flex flex-col gap-3 pt-2">
          {items.length === 0 ? (
            <Empty className="min-h-[8rem] p-6">
              <EmptyMedia variant="icon">
                <ClipboardList />
              </EmptyMedia>
              <EmptyDescription>
                No questions yet. Add questions to collect details after a booking is confirmed.
              </EmptyDescription>
            </Empty>
          ) : (
            <div className="flex flex-col gap-3">
              {items.map((item, index) => (
                <QuestionCard
                  key={item.id}
                  item={item}
                  index={index}
                  isFirst={index === 0}
                  isLast={index === items.length - 1}
                  onChange={(patch) => onChange(updateItem(items, item.id, patch))}
                  onMove={(direction) => onChange(moveItem(items, index, direction))}
                  onRemove={() => onChange(items.filter((candidate) => candidate.id !== item.id))}
                />
              ))}
            </div>
          )}
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="self-start"
            onClick={() => onChange([...items, createDraftItem()])}
          >
            <Plus data-icon="inline-start" />
            Add question
          </Button>
        </TabsContent>
        <TabsContent value="preview" className="pt-2">
          <QuestionnairePreview items={items} />
        </TabsContent>
      </Tabs>
    </div>
  )
}

function QuestionCard({
  item,
  index,
  isFirst,
  isLast,
  onChange,
  onMove,
  onRemove,
}: {
  item: BookingQuestionnaireDraftItem
  index: number
  isFirst: boolean
  isLast: boolean
  onChange: (patch: Partial<BookingQuestionnaireDraftItem> | ((current: BookingQuestionnaireDraftItem) => BookingQuestionnaireDraftItem)) => void
  onMove: (direction: 'up' | 'down') => void
  onRemove: () => void
}) {
  const questionNumber = index + 1
  const promptId = `questionnaire-prompt-${item.id}`
  const descriptionId = `questionnaire-description-${item.id}`
  const typeId = `questionnaire-type-${item.id}`
  const requiredId = `questionnaire-required-${item.id}`

  return (
    <div className="flex flex-col gap-3 rounded-lg border p-3">
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs font-medium text-muted-foreground">
          Question {questionNumber}
        </p>
        <div className="flex items-center">
          <Button
            type="button"
            variant="ghost"
            size="icon-xs"
            aria-label={`Move question ${questionNumber} up`}
            disabled={isFirst}
            onClick={() => onMove('up')}
          >
            <ArrowUp />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon-xs"
            aria-label={`Move question ${questionNumber} down`}
            disabled={isLast}
            onClick={() => onMove('down')}
          >
            <ArrowDown />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon-xs"
            aria-label={`Remove question ${questionNumber}`}
            className="text-destructive hover:text-destructive"
            onClick={onRemove}
          >
            <X />
          </Button>
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <Label htmlFor={promptId}>
          Prompt <span className="text-destructive">*</span>
        </Label>
        <Input
          id={promptId}
          value={item.prompt}
          onChange={(event) => onChange({ prompt: event.target.value })}
          placeholder="e.g., Where should the team park?"
          autoComplete="off"
          spellCheck={false}
          data-1p-ignore
        />
      </div>

      <div className="flex flex-col gap-2">
        <Label htmlFor={descriptionId}>Help text</Label>
        <Input
          id={descriptionId}
          value={item.description}
          onChange={(event) => onChange({ description: event.target.value })}
          placeholder="Optional context shown under the question"
          autoComplete="off"
          spellCheck={false}
          data-1p-ignore
        />
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="flex min-w-[12rem] flex-1 flex-col gap-2">
          <Label htmlFor={typeId}>Type</Label>
          <select
            id={typeId}
            aria-label={`Type for question ${questionNumber}`}
            value={item.type}
            onChange={(event) => {
              const nextType = Number(event.target.value) as QuestionTypeEnum
              onChange((current) => withQuestionType(current, nextType))
            }}
            className={cn(
              'border-input dark:bg-input/30 h-9 w-full rounded-md border bg-transparent px-3 py-1 text-sm shadow-xs outline-none',
              'focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]',
            )}
          >
            {BOOKING_QUESTION_TYPES.map((entry) => (
              <option key={entry.type} value={entry.type}>
                {entry.label}
              </option>
            ))}
          </select>
        </div>
        <div className="flex items-center gap-2 pt-6">
          <Switch
            id={requiredId}
            checked={item.required}
            onCheckedChange={(checked) => onChange({ required: checked })}
          />
          <Label htmlFor={requiredId}>Required</Label>
        </div>
      </div>

      {bookingQuestionNeedsOptions(item.type) && (
        <OptionsEditor
          questionNumber={questionNumber}
          options={item.options}
          onChange={(options) => onChange({ options })}
        />
      )}
    </div>
  )
}

function OptionsEditor({
  questionNumber,
  options,
  onChange,
}: {
  questionNumber: number
  options: BookingQuestionnaireDraftItem['options']
  onChange: (options: BookingQuestionnaireDraftItem['options']) => void
}) {
  return (
    <div className="flex flex-col gap-2">
      <Label>Options</Label>
      <div className="flex flex-col gap-2">
        {options.map((option, index) => (
          <div key={option.id} className="flex items-center gap-2">
            <Input
              aria-label={`Question ${questionNumber} option ${index + 1}`}
              value={option.label}
              onChange={(event) => {
                onChange(options.map((candidate) => (
                  candidate.id === option.id
                    ? { ...candidate, label: event.target.value }
                    : candidate
                )))
              }}
              placeholder={`Option ${index + 1}`}
              autoComplete="off"
              spellCheck={false}
              data-1p-ignore
            />
            <Button
              type="button"
              variant="ghost"
              size="icon-xs"
              aria-label={`Remove question ${questionNumber} option ${index + 1}`}
              disabled={options.length <= 1}
              onClick={() => onChange(options.filter((candidate) => candidate.id !== option.id))}
            >
              <X />
            </Button>
          </div>
        ))}
      </div>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="self-start"
        onClick={() => onChange([...options, createDraftOption('')])}
      >
        <Plus data-icon="inline-start" />
        Add option
      </Button>
    </div>
  )
}

function QuestionnairePreview({
  items,
}: {
  items: BookingQuestionnaireDraftItem[]
}) {
  if (items.length === 0) {
    return (
      <Empty className="min-h-[8rem] p-6">
        <EmptyDescription>
          Add a question to preview how it will look for the customer.
        </EmptyDescription>
      </Empty>
    )
  }

  return (
    <div className="flex flex-col gap-4 rounded-lg border p-4">
      {items.map((item, index) => (
        <PreviewQuestion key={item.id} item={item} index={index} />
      ))}
    </div>
  )
}

function PreviewQuestion({
  item,
  index,
}: {
  item: BookingQuestionnaireDraftItem
  index: number
}) {
  const prompt = item.prompt.trim() || `Question ${index + 1}`
  const promptId = `questionnaire-preview-${item.id}`

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-col gap-1">
        <Label htmlFor={promptId}>
          {prompt}
          {item.required && <span className="text-destructive"> *</span>}
        </Label>
        {item.description.trim() && (
          <p className="text-sm text-muted-foreground">{item.description.trim()}</p>
        )}
      </div>
      <PreviewControl item={item} promptId={promptId} prompt={prompt} />
    </div>
  )
}

function PreviewControl({
  item,
  promptId,
  prompt,
}: {
  item: BookingQuestionnaireDraftItem
  promptId: string
  prompt: string
}) {
  const labeledOptions = item.options.filter((option) => option.label.trim() !== '')

  switch (questionWidgetKind(item.type)) {
    case 'textarea':
      return (
        <Textarea
          id={promptId}
          disabled
          placeholder="Long answer"
          aria-label={prompt}
        />
      )
    case 'tel':
      return (
        <Input
          id={promptId}
          type="tel"
          disabled
          placeholder="(555) 555-5555"
          aria-label={prompt}
        />
      )
    case 'email':
      return (
        <Input
          id={promptId}
          type="email"
          disabled
          placeholder="name@example.com"
          aria-label={prompt}
        />
      )
    case 'address':
      return (
        <Input
          id={promptId}
          disabled
          placeholder="Search for an address"
          aria-label={prompt}
        />
      )
    case 'boolean':
      return (
        <RadioGroup disabled aria-label={prompt} className="gap-2">
          <div className="flex items-center gap-2">
            <RadioGroupItem value="yes" id={`${promptId}-yes`} />
            <Label htmlFor={`${promptId}-yes`}>Yes</Label>
          </div>
          <div className="flex items-center gap-2">
            <RadioGroupItem value="no" id={`${promptId}-no`} />
            <Label htmlFor={`${promptId}-no`}>No</Label>
          </div>
        </RadioGroup>
      )
    case 'choice':
      return (
        <RadioGroup disabled aria-label={prompt} className="gap-2">
          {labeledOptions.map((option) => (
            <div key={option.id} className="flex items-center gap-2">
              <RadioGroupItem value={option.id} id={`${promptId}-${option.id}`} />
              <Label htmlFor={`${promptId}-${option.id}`}>{option.label.trim()}</Label>
            </div>
          ))}
        </RadioGroup>
      )
    case 'files':
      return (
        <div
          role="group"
          aria-label={prompt}
          className="flex flex-col items-center gap-1 rounded-lg border-2 border-dashed px-6 py-6 text-center text-muted-foreground"
        >
          <UploadCloud className="size-6" aria-hidden />
          <p className="text-sm">
            {item.type === QuestionTypeEnum.FILE_UPLOAD_MULTIPLE
              ? 'Customers drag and drop up to 10 files here'
              : 'Customers drag and drop one file here'}
          </p>
        </div>
      )
    case 'multi':
      return (
        <div role="group" aria-label={prompt} className="flex flex-col gap-2">
          {labeledOptions.map((option) => (
            <div key={option.id} className="flex items-center gap-2">
              <Checkbox id={`${promptId}-${option.id}`} disabled />
              <Label htmlFor={`${promptId}-${option.id}`}>{option.label.trim()}</Label>
            </div>
          ))}
        </div>
      )
    default:
      return (
        <Input
          id={promptId}
          disabled
          placeholder="Short answer"
          aria-label={prompt}
        />
      )
  }
}
